import * as Icons from "@ant-design/icons";
import { IconBaseProps } from "@ant-design/icons/lib/components/Icon";
import { Col, Grid, Layout, Row, Select, Spin, theme, Typography } from "antd";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUser } from "../../apis/apis";
import { db } from "../../config/firebase";
import { AIProvider, Category, Prompt, User } from "../../models/models";
import AppHeader from "../layout/AppHeader";
import PromptCard from "../PromptCard/PromptCard";
import "./PromptsPageContent.css";


const { Title } = Typography;
const { useBreakpoint } = Grid;
const { useToken } = theme;

type AntdIconName = keyof typeof Icons;
function getAntdIconComponent(
  iconName: string
): React.FC<IconBaseProps> | null {
  const iconComponent = Icons[iconName as AntdIconName];
  if (
    typeof iconComponent === "function" ||
    typeof iconComponent === "object"
  ) {
    return iconComponent as React.FC<IconBaseProps>;
  }
  return null;
}

function getCategoryIcon(record: Category) {
  const IconComponent = getAntdIconComponent(record.icon || "");
  return IconComponent ? <IconComponent style={{ fontSize: 24 }} /> : null;
}

const PromptsPageContent: React.FC = () => {
  const screens = useBreakpoint();
  const { token } = useToken();
  const isMobile = !screens.md;
  const defaultHeaderHeight = isMobile ? 52 : 64;
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<any[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [fullCategories, setFullCategories] = useState<Category[]>([]);
  const [sortOrder, setSortOrder] = useState<string>("all");
  const [aiModels, setAiModels] = useState<any[]>([]);
  const [curretUser, setCurrentUser] = useState<User | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>("");
  const [selectedCategoryIcon, setSelectedCategoryIcon] =
    useState<React.ReactNode>(null);
  const [headerHeight, setHeaderHeight] =
    useState<number>(defaultHeaderHeight);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (typeof document === "undefined") return;
    const previousBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = token.colorBgBase;

    return () => {
      document.body.style.backgroundColor = previousBg;
    };
  }, [token.colorBgBase]);


  console.log("sleectedCat", selectedCategory)


  const sortOptions = [
    { label: t("prompts.all"), value: "all" },
    { label: t("prompts.trending"), value: "trending" },
    { label: t("prompts.new"), value: "new" },
    { label: t("prompts.bestRated"), value: "bestRated" },
  ];


  const handleSortChange = (next: any) => {
    const value = typeof next === "string" ? next : next?.target?.value;
    setSortOrder(value);
  };

  useEffect(() => {
    async function fetchUser() {
      const user = await getUser();
      setCurrentUser(user);
    }
    fetchUser();
  }, []);

  useEffect(() => {
    const providersRef = collection(
      db,
      `Clients/${curretUser?.clientId}/AIProviders`
    );
    const q = query(providersRef);
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const providersData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as AIProvider[];
      setAiModels(
        providersData.map((provider) => ({
          value: provider.name,
          label: provider.name,
        }))
      );
    });
    return () => unsubscribe();
  }, [curretUser]);

  useEffect(() => {
    const fetchFullCategories = async () => {
      try {
        const collRef = collection(db, "Categories");
        const querySnapshot = await getDocs(collRef);
        const categories = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Category[];
        console.log("AllCategories", categories)
        setFullCategories(categories);
      } catch (error) {
        console.error("Error fetching data: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFullCategories();
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const fetchUserAndCategories = async () => {
      const user = await getUser();
      if (!user?.clientId || !user?.id) {
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, `Clients/${user.clientId}/Users/${user.id}`);
        unsubscribe = onSnapshot(docRef, (docSnapshot) => {
          const data = docSnapshot.data();
          if (data && data.categories) {
            const categoriesIds = data.categories as string[];

            // Filter fullCategories to get only user's categories
            let userCategories = fullCategories.filter((cat) =>
              categoriesIds.includes(cat.id)
            );

            // userCategories = userCategories.filter(uc => uc.name[i18n.language])


            // Map categories with localized labels
            let userCategoriesWithLabels = userCategories.map((c) => ({
              value: c.id,
              label: c.name?.[i18n.language],
            }));


            userCategoriesWithLabels = userCategoriesWithLabels.filter(uc => uc.label)



            console.log("userCategoriesWithLabels", userCategoriesWithLabels)

            setCategories(userCategoriesWithLabels);
          }
          setLoading(false);
        });
      } catch (error) {
        console.error("Error fetching data: ", error);
        setLoading(false);
      }
    };

    fetchUserAndCategories();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [i18n.language, fullCategories]);

  useEffect(() => {
    if (loading || categories.length === 0) return;
    setLoading(true);

    const fetchPromptsFromCategories = async (categoryIds: string[]) => {
      try {
        const promptsRef = collection(db, "Prompts");
        let allPrompts: Prompt[] = [];

        const categoryPromises = categoryIds.map(async (categoryId) => {
          const categoryDocRef = doc(db, "Categories", categoryId);
          const categoryDoc = await getDoc(categoryDocRef);
          if (categoryDoc.exists()) {
            const categoryData = categoryDoc.data() as Category;
            if (categoryData.prompts.length > 0) {
              const batchQueries = [];
              for (let i = 0; i < categoryData.prompts.length; i += 10) {
                const batch = categoryData.prompts.slice(i, i + 10);
                batchQueries.push(
                  query(promptsRef, where("__name__", "in", batch))
                );
              }
              const promptSnapshots = await Promise.all(
                batchQueries.map(getDocs)
              );
              const fetchedPrompts = promptSnapshots.flatMap(
                (snapshot) =>
                  snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                  })) as Prompt[]
              );
              allPrompts = [...allPrompts, ...fetchedPrompts];
            }
          }
        });

        await Promise.all(categoryPromises);

        const metricsPromises = allPrompts.map(async (prompt) => {
          const promptMetricsRef = doc(
            db,
            `Clients/${curretUser?.clientId}/Prompts/${prompt.id}`
          );
          const promptMetricsDoc = await getDoc(promptMetricsRef);
          if (promptMetricsDoc.exists()) {
            const promptMetrics = promptMetricsDoc.data();
            prompt.count = promptMetrics?.count ?? 0;
            prompt.rateValue = promptMetrics?.rateValue ?? 0;
          }
          return prompt;
        });

        const promptsWithMetrics = await Promise.all(metricsPromises);
        const uniquePrompts = Array.from(
          new Map(promptsWithMetrics.map((item) => [item.id, item])).values()
        );
        let sortedPrompts = sortPrompts(uniquePrompts);

        sortedPrompts = sortedPrompts.filter(p => p.name[i18n.language] && p.description[i18n.language] && p.prompt[i18n.language])


        console.log("sortedPrompts", sortedPrompts)
        setPrompts(sortedPrompts);
      } catch (error) {
        console.error("Error fetching prompts: ", error);
      } finally {
        setLoading(false);
      }
    };

    if (selectedCategory !== "") {
      fetchPromptsFromCategories([selectedCategory]);
    } else {
      const categoryIds = categories.map((category) => category.value);
      fetchPromptsFromCategories(categoryIds);
    }
  }, [selectedCategory, sortOrder, categories]); // eslint-disable-line

  const sortPrompts = (prompts: Prompt[]): Prompt[] => {
    switch (sortOrder) {
      case "trending":
        return prompts
          .filter((p) => p.count !== undefined)
          .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
          .concat(prompts.filter((p) => p.count === undefined));
      case "new":
        return prompts.sort(
          (a, b) =>
            dateFromTimestamp(b.createdAt as Timestamp) -
            dateFromTimestamp(a.createdAt as Timestamp)
        );
      case "bestRated":
        return prompts
          .filter((p) => p.rateValue !== undefined)
          .sort((a, b) => (b.rateValue ?? 0) - (a.rateValue ?? 0))
          .concat(prompts.filter((p) => p.rateValue === undefined));
      case "all":
      default:
        return prompts;
    }
  };

  const dateFromTimestamp = (timestamp: Timestamp) =>
    timestamp.toDate().getTime();

  useEffect(() => {
    if (!selectedCategory) {
      setSelectedCategoryName("");
      setSelectedCategoryIcon(null);
      return;
    }

    const category = fullCategories.find((cat) => cat.id === selectedCategory);
    if (!category) {
      setSelectedCategoryName("");
      setSelectedCategoryIcon(null);
      return;
    }

    setSelectedCategoryName(category.name?.[i18n.language] ?? "");
    setSelectedCategoryIcon(getCategoryIcon(category));
  }, [selectedCategory, i18n.language, fullCategories]);

  const handleCategoryChange = (value?: string) => {
    setSelectedCategory(value || "");
  };

  if (aiModels.length === 0) {
    return (
      <Layout style={{ backgroundColor: token.colorBgBase }}>
        <Row
          justify="center"
          align="middle"
          style={{ minHeight: "50vh", padding: 24 }}
        >
          <Col>
            <Title level={screens.xs ? 5 : 3}>
              {t("prompts.noModelsAvailable")}
            </Title>
          </Col>
        </Row>
      </Layout>
    );
  }

  const contentHeight =
    headerHeight > 0 ? `calc(100svh - ${headerHeight}px)` : "100svh";

  return (
    <Layout
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100svh",
        background: token.colorBgBase,
      }}
    >
      <AppHeader
        position={isMobile ? "fixed" : "sticky"}
        onHeightChange={setHeaderHeight}
      />


      <Row
        style={{
          height: contentHeight,
          marginTop: isMobile ? headerHeight : 0,
          backgroundColor: token.colorBgBase,
        }}
      >
        <Col
          span={24}
          style={{
            height: contentHeight,
            backgroundColor: token.colorBgBase,
          }}
        >
          <div
            style={{
              overflowY: "auto",
              height: contentHeight,
              padding: 25,
              paddingTop: 25,
              paddingBottom: 80,
              backgroundColor: token.colorBgBase,
            }}
          >
            <Spin spinning={loading}>
              <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Select
                    placeholder={t('prompts.filterByCategory')}
                    onChange={handleCategoryChange}
                    options={[
                      { label: t('prompts.all'), value: "" },
                      ...categories.map((c) => ({
                        label: c.label,
                        value: c.value,
                      })),
                    ]}
                    style={{ width: "100%" }}
                    allowClear
                  />
                </Col>
                <Col xs={24} md={8} lg="auto">
                  <Select
                    value={sortOrder}
                    onChange={handleSortChange}
                    options={sortOptions}
                    style={{ width: "100%" }}
                  />
                </Col>
              </Row>

              {/* Título */}
              <Row style={{ marginBottom: 16 }}>
                <Col span={24}>
                  <Title level={2} style={{ marginTop: 8, marginBottom: 0 }}>
                    {selectedCategoryIcon}{" "}
                    {selectedCategoryName
                      ? t("prompts.promptsOf", { category: selectedCategoryName })
                      : t("prompts.prompts")}
                  </Title>
                </Col>
              </Row>

              {/* GRID de tarjetas (responsivo) */}
              <Row gutter={[16, 16]}>
                {prompts.map((prompt) => (
                  <Col
                    key={prompt.id}
                    xs={24}
                    sm={12}
                    md={12}
                    lg={8}
                    xl={6}
                    xxl={6}
                  >
                    <PromptCard
                      categories={fullCategories}
                      category={selectedCategory}
                      prompt={prompt}
                      aiModels={aiModels}
                      clientId={curretUser?.clientId}
                    />
                  </Col>
                ))}
              </Row>

            
            </Spin>
          </div>
        </Col>
      </Row>
    </Layout>
  );
};

export default PromptsPageContent;
